/**
 * Admin - Vibe Tenant Cluster Status History (read)
 *
 * How long each tenant cluster spent in each roster state over a window, read
 * off the append-only `VibeTenantClusterStatusTransition` history — so "what
 * share of the window was the fleet degraded, and was it a pending config diff
 * or a fault" is one call rather than a reconstruction from Pulumi history and
 * tick logs.
 *
 * The roster state is the SAME axis the fleet roster renders
 * (`VibeTenantClusterRosterState`), so `PENDING_CONFIG_DIFF` here is exactly the
 * benign pending-diff chip there and `DEGRADED` the fault chip.
 *
 * UNRECORDED IS NOT A STATE. History starts the moment the table was created and
 * the first write after that moment records each cluster's first known state; the
 * time before it is `unrecordedSeconds`, never any roster state. Every share is
 * over RECORDED time, and is null when nothing in the window was recorded.
 */

import { z } from "zod";

import { VibeTenantClusterRosterStateReadSchema } from "../../../shared/domain/vibe/tenant-cluster-roster-state";
import { VIBE_STATUS_HISTORY_MAX_WINDOW_DAYS } from "../../../shared/domain/vibe/tenant-cluster-status-history";
import { ApiSuccessSchema } from "../../common/response.schema";

const DAY_MS = 24 * 60 * 60 * 1000;

export const AdminVibeTenantClusterStatusHistoryQuerySchema = z
  .object({
    /** Window start, ISO 8601. Defaults to `to` minus the default window. */
    from: z.string().datetime().optional(),
    /** Window end, ISO 8601, exclusive. Defaults to now. */
    to: z.string().datetime().optional(),
    /** One tenant. Omitted: the whole fleet. */
    organizationId: z.string().min(1).optional()
  })
  // `Date.parse`, never a string comparison: two ISO instants with different UTC
  // offsets do not order lexically.
  .refine(
    (q) => q.from === undefined || q.to === undefined || Date.parse(q.from) < Date.parse(q.to),
    {
      message: "`from` must be before `to`"
    }
  )
  .refine(
    (q) =>
      q.from === undefined ||
      q.to === undefined ||
      Date.parse(q.to) - Date.parse(q.from) <= VIBE_STATUS_HISTORY_MAX_WINDOW_DAYS * DAY_MS,
    { message: `the window may span at most ${VIBE_STATUS_HISTORY_MAX_WINDOW_DAYS} days` }
  );

export type AdminVibeTenantClusterStatusHistoryQuery = z.infer<
  typeof AdminVibeTenantClusterStatusHistoryQuerySchema
>;

/**
 * Seconds spent in each roster state. Every state the backend lists is present,
 * zero included. Keyed by the READ vocabulary, so a state a newer backend adds
 * arrives as one more key instead of failing the whole history read.
 */
export const RosterStateSecondsSchema = z.record(
  VibeTenantClusterRosterStateReadSchema,
  z.number()
);

/**
 * The degraded share of RECORDED time, split by roster state. Each is a fraction
 * in [0, 1], or null when the window holds no recorded time to divide by.
 * `degraded` is the sum of the other three.
 */
export const DegradedShareSchema = z.object({
  degraded: z.number().nullable(),
  pendingConfigDiff: z.number().nullable(),
  fault: z.number().nullable(),
  unclassified: z.number().nullable()
});

export const RosterTimeSchema = z.object({
  recordedSeconds: z.number(),
  unrecordedSeconds: z.number(),
  secondsByState: RosterStateSecondsSchema,
  share: DegradedShareSchema
});

export const AdminVibeTenantClusterStatusHistoryTenantSchema = RosterTimeSchema.extend({
  organizationId: z.string(),
  organizationName: z.string().nullable()
});

export const AdminVibeTenantClusterStatusHistorySchema = z.object({
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  windowSeconds: z.number(),
  /** The first transition anywhere in the history, or null if there is none yet. */
  recordingStartedAt: z.string().datetime().nullable(),
  tenants: z.array(AdminVibeTenantClusterStatusHistoryTenantSchema),
  /** Every tenant's time summed — the fleet's share is tenant-time weighted. */
  fleet: RosterTimeSchema
});

export type AdminVibeTenantClusterStatusHistory = z.infer<
  typeof AdminVibeTenantClusterStatusHistorySchema
>;

export const AdminVibeTenantClusterStatusHistoryEndpoint = {
  method: "GET" as const,
  path: "/api/admin/vibe/tenant-cluster/status-history",
  Query: AdminVibeTenantClusterStatusHistoryQuerySchema,
  Response: ApiSuccessSchema(AdminVibeTenantClusterStatusHistorySchema)
};
